import { ApolloLink, ServerError } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { bumpInvalidCsrfTokenReloadCount } from "@oysterhr/identity/CsrfContext"
import { addBreadcrumb, setTag } from "@sentry/browser"
import { SESSION_EXPIRED_BREADCRUMB } from "../initSentry/utils"

const UNAUTHORIZED = "401"
const FORBIDDEN = "403"
const UNPROCESSABLE_ENTITY = "422"

type AuthMiddlewareConfig = {
  csrfToken?: string | null
  unauthenticatedRedirect?: string
}

export function authMiddleware({
  csrfToken,
  unauthenticatedRedirect = "/users/sign_in",
}: AuthMiddlewareConfig) {
  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext({
      fetchOptions: {
        credentials: "include",
      },
      headers: {
        "X-From-Page": window.location.pathname,
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
    })

    addBreadcrumb({
      message: `Requested ${operation?.operationName}, with variables: ${JSON.stringify(
        operation?.variables,
      )}`,
      type: "graphql",
      level: "info",
    })

    return forward(operation)
  })

  const unauthorizedErrorHandler = onError(({ networkError, forward, operation }) => {
    if (networkError?.message.includes(UNPROCESSABLE_ENTITY)) {
      const response = (networkError as ServerError)?.result as Record<string, string>

      if (response?.error_code === "INVALID_CSRF_TOKEN") {
        setTag("safeToIgnore", true)

        bumpInvalidCsrfTokenReloadCount()
        window.location.reload()
      }
    }

    if (networkError?.message.includes(UNAUTHORIZED) || networkError?.message.includes(FORBIDDEN)) {
      addBreadcrumb({
        message: `${SESSION_EXPIRED_BREADCRUMB} ${networkError?.message}`,
        type: "graphql",
        level: "info",
      })

      window.location.href = unauthenticatedRedirect
    }

    return forward(operation)
  })

  return authMiddleware.concat(unauthorizedErrorHandler)
}
